import React from "react";
import { FormElement } from "./FormBuilder.types";
import {
  Input,
  CPFInput,
  DocumentInput,
  PhoneInput,
  DateInput,
} from "../Input/Input";
import { Select } from "../Select/Select";
import { InputHidden } from "../InputHidden/InputHidden";
import { Textarea } from "../Textarea/Textarea";
import { CepAddressField } from "../CepAddressField/CepAddressField";
import { RadioGroup } from "../RadioGroup/RadioGroup";
import { CheckboxGroup } from "../CheckboxGroup/CheckboxGroup";
import FileUploadInput from '../FileUploadInput/FileUploadInput';

interface FormFieldRendererProps {
  element: FormElement;
  validationErrors: Record<string, boolean>;
  handleFieldChange: (fieldName: string) => void;
  cepState: string;
  setCepState: (state: string) => void;
  stateValue: string;
  city: string;
  formStyles?: { textColor?: string };
  privacyPolicy: string;
  dynamicValue?: string;
  unitSelectOptions?: Array<{ value: string; label: string }> | null;
}

const FormFieldRenderer: React.FC<FormFieldRendererProps> = ({
  element,
  validationErrors,
  handleFieldChange,
  cepState,
  setCepState,
  stateValue,
  city,
  formStyles,
  privacyPolicy,
  dynamicValue,
  unitSelectOptions,
}) => {
  const { type, props } = element;
  const fieldId = `form-field-${props.attribute_name || type}-${element.id}`;
  const fieldName = props.attribute_name;
  const hasError = fieldName ? validationErrors[fieldName] : false;
  const helperText = hasError
    ? props.validation?.errorMessage || "Este campo precisa ser preenchido."
    : "";

  if (unitSelectOptions && unitSelectOptions.length > 0) {
    return (
      <Select
        key={fieldId}
        id={fieldId}
        label={props.label || 'Selecione a unidade'}
        name={props.attribute_name}
        required={props.validation?.required}
        error={hasError}
        helperText={helperText}
        colorLabel={formStyles?.textColor}
        onChange={() => fieldName && handleFieldChange(fieldName)}
        options={[
          { value: '', label: 'Selecione a unidade' },
          ...unitSelectOptions,
        ]}
      />
    );
  }

  if (dynamicValue && props.hidden) {
    return (
      <InputHidden
        key={fieldId}
        name={props.attribute_name}
        value={dynamicValue}
      />
    );
  }

  if (props.hidden) {
    return (
      <InputHidden
        key={fieldId}
        name={props.attribute_name}
        value={props.value || ""}
      />
    );
  }

  const commonProps = {
    key: fieldId,
    id: fieldId,
    label: props.label,
    placeholder: props.placeholder,
    name: props.attribute_name,
    required: props.validation?.required,
    error: hasError,
    helperText: helperText,
    colorLabel: formStyles?.textColor,
    onChange: () => fieldName && handleFieldChange(fieldName),
  };

  const fieldMap: { [key: string]: React.ReactNode } = {
    'text_input': <Input {...commonProps} />,
    'email_input': <Input {...commonProps} />,
    'cpf-input': <CPFInput {...commonProps} />,
    'document-input': <DocumentInput {...commonProps} />,
    'phone-input': <PhoneInput {...commonProps} />,
    'date-input': <DateInput {...commonProps} />,
    'textarea': <Textarea {...commonProps} rows={props.rows} />,
    'select': <Select {...commonProps} options={props.options || []} />,
    'cep-input': (
      <CepAddressField
        element={element}
        commonProps={commonProps}
        cepState={cepState}
        setCepState={setCepState}
        stateValue={stateValue}
        city={city}
        formStyles={formStyles}
        handleFieldChange={handleFieldChange}
      />
    ),
    'radio': (
      <RadioGroup
        element={element}
        commonProps={commonProps}
        formStyles={formStyles}
        handleFieldChange={handleFieldChange}
      />
    ),
    'checkbox': (
      <CheckboxGroup
        element={element}
        commonProps={commonProps}
        formStyles={formStyles}
        privacyPolicy={privacyPolicy}
        handleFieldChange={handleFieldChange}
      />
    ),
    'file_upload': (
      <FileUploadInput
        {...commonProps}
        accept={props.accept}
        maxSize={props.maxSize}
        uploadUrl={process.env.NEXT_PUBLIC_UPLOAD_FILE_URL}
      />
    ),
  };

  return <>{fieldMap[type] || null}</>;
};

export default FormFieldRenderer;
